import { useSelector } from "react-redux";
import { getDictionary } from "../services/siteService";
import { getOrderDetail } from "../services/orderService";
import { selectUserState } from "../selectors/application";
import { format, MathUtils } from "../utils";
import { dictTypes, orderTypes } from "../utils/lynkco-constants";
import { getLang } from "../utils/processesOrderDetails";
import getSiteInfo from "../utils/getSiteInfo";
import { getRetrieveMembership } from "../services/memberShipService";
import { gQuery } from "../Graphql/gQuery";
import { queryMemberShipList } from "../Graphql/queryMembershipList";

const useMembershipOrderDetail = ({ fields, match }) => {
  const user = useSelector(selectUserState);
  const { monthToMonthItem, longTermItem, loanItem, cashItem } = fields;

  const setTimeFormat = async orderTime => {
    if (!orderTime) return null;

    const siteInfo = await getSiteInfo();
    return format(orderTime, siteInfo.dateFormat);
  };

  const setSummaryItem = (itemsProps, details) => {
    let summaryItems = [];
    let language = details?.country ? `${details?.language}-${details?.country}` : details?.language;
    const itemLabels = (itemsProps || []).map(i => ({
      key: i.fields.key.value,
      value: i.fields.phrase.value
    }));
    itemLabels.forEach(item => {
      let itemObj = {
        title: `${item.value}:`,
        value: details?.payMethod || ""
      };
      switch (item?.key) {
        case "loremIpsum":
          summaryItems.push(
            {
              title: `${item.value}:`,
              value: details?.payMethod || ""
            },
            {
              title: "",
              value: details?.cardNo || ""
            }
          );
          break;
        case "cardNo":
          itemObj.value = details[item?.key] || "";
          summaryItems.push(itemObj);
          break;
        case "grandTotal":
          itemObj.value = MathUtils.priceFormat(details[item?.key], language) || "";
          summaryItems.push(itemObj);
          break;
        case "totalPayment":
          itemObj.value = details?.monthlyPaymentInformation ? MathUtils.priceFormat(details?.monthlyPaymentInformation[item?.key], language) || "" : "";
          summaryItems.push(itemObj);
          break;
        default:
          break;
      }
    });
    return summaryItems;
  };

  const setNextPaymentItem = detail => {
    let nextPaymentItems = [];
    if (detail && detail?.monthlyPaymentInformation) {
      const { additionalMonthlyPaymentList, basicMonthlyPaymentList } = detail?.monthlyPaymentInformation;
      let language = detail?.country ? `${detail?.language}-${detail?.country}` : detail?.language;
      basicMonthlyPaymentList.forEach(basicItem => {
        let item = {
          title: basicItem?.businessName,
          value: MathUtils.priceFormat(basicItem?.paymentTotalAmount, language)
        };
        nextPaymentItems.push(item);
      });
      additionalMonthlyPaymentList.forEach(additionalItem => {
        let item = {
          title: additionalItem?.businessName,
          value: MathUtils.priceFormat(additionalItem?.paymentTotalAmount, language)
        };
        nextPaymentItems.push(item);
      });
    }
    return nextPaymentItems;
  };
  const getSiteMemberShipList = async () => {
    let siteMemberShipList = [];
    const {
      data: { obj }
    } = await gQuery({
      query: queryMemberShipList,
      variables: {
        language: match.params.lang
      }
    });
    if (!obj) {
      siteMemberShipList = [];
    } else {
      siteMemberShipList = obj.list;
    }
    return { siteMemberShipList };
  };
  const setCepApiMeberShip = async (currentMebership, orderVin) => {
    const { siteMemberShipList } = await getSiteMemberShipList();
    const mebshipListCep = await getRetrieveMembership(user?.customerNumber || "");

    let membershipOverview = {
      ...currentMebership?.membershipOverview,
      overview: {
        ...currentMebership?.membershipOverview?.overview,
        body: "",
        renewDate: "",
        heading: orderVin ? { value: orderVin } : currentMebership?.membershipOverview?.overview?.heading
      }
    };
    let currentCepMebship = mebshipListCep?.filter(x => x.VIN === orderVin)[0];
    let orderTime = currentCepMebship?.RenewalDate ? await setTimeFormat(currentCepMebship.RenewalDate) : null;
    let siteMemberShip = siteMemberShipList.filter(item => item.key.value === currentCepMebship?.MembershipCode)[0]?.name?.value;
    if (!currentCepMebship) {
      membershipOverview = null;
    } else {
      membershipOverview = {
        ...membershipOverview,
        overview: {
          ...membershipOverview?.overview,
          body: orderTime ? currentMebership.membershipOverview.overview.body : "",
          renewDate: orderTime || "",
          subheading: siteMemberShip
        }
      };
    }
    return membershipOverview;
  };
  const setNextPaymentAndSummary = async currentMebership => {
    const orderTypeDic = await getDictionary(dictTypes.orderType);
    if (!currentMebership?.orderId) {
      return currentMebership;
    }

    const orderDetail = await getOrderDetail(currentMebership?.orderId);
    if (!orderDetail) return null;
    let orderType = orderTypeDic?.filter(x => x.dicCode === orderDetail.subType)[0]?.languages[getLang(match.params.lang)]?.dicName;
    let orderVin = orderDetail?.orderExtraData?.vin || null;
    let orderSantanderURL = orderDetail?.orderExtraData?.santanderURL || null;
    const { dueDate } = orderDetail?.monthlyPaymentInformation || {};
    const nextPaymentItems = setNextPaymentItem(orderDetail);
    let orderTime = (await setTimeFormat(dueDate)) || "";
    let currentCepMebership = await setCepApiMeberShip(currentMebership, orderVin);
    let summaryItem = [];
    let language = orderDetail?.country ? `${orderDetail?.language}-${orderDetail?.country}` : orderDetail?.language;
    let membershipOverview = {
      ...currentMebership?.membershipOverview,
      overview: {
        ...currentMebership?.membershipOverview?.overview,
        body: "",
        renewDate: "",
        heading: orderVin ? { value: orderVin } : currentMebership?.membershipOverview?.overview?.heading,
        subheading: orderType
      }
    };
    let paymentDetails = {};
    let needHelp = {
      ...currentMebership?.needHelp,
      link: {
        ...currentMebership?.needHelp?.link
      }
    };
    switch (orderDetail.subType) {
      case orderTypes.gear:
        break;
      case orderTypes.presale:
        break;
      case orderTypes.monthToMonth:
        summaryItem = setSummaryItem(monthToMonthItem?.fields?.items, orderDetail);
        break;
      case orderTypes.longTerm:
        if (orderSantanderURL) {
          needHelp = {
            ...needHelp,
            link: {
              ...needHelp.link,
              link: {
                ...needHelp.link.link,
                value: {
                  ...needHelp.link.link.value,
                  href: orderSantanderURL
                }
              }
            }
          };
        } else {
          needHelp = {
            ...needHelp,
            link: null
          };
        }
        summaryItem = setSummaryItem(longTermItem?.fields?.items, orderDetail);
        break;
      case orderTypes.buyACarLoan:
        if (nextPaymentItems.length) {
          nextPaymentItems.unshift({
            title: loanItem?.fields?.loanInstallment?.value,
            value: MathUtils.priceFormat(orderDetail?.orderExtraData?.loanMonthlyFee || 0, language)
          });
          summaryItem = setSummaryItem(loanItem?.fields?.items, orderDetail);
        }
        if (orderSantanderURL) {
          needHelp = {
            ...needHelp,
            link: {
              ...needHelp.link,
              link: {
                ...needHelp.link.link,
                value: {
                  ...needHelp.link.link.value,
                  href: orderSantanderURL
                }
              }
            }
          };
        } else {
          needHelp = {
            ...needHelp,
            link: null
          };
        }
        break;
      case orderTypes.buyACarCash:
        summaryItem = setSummaryItem(cashItem?.fields?.items, orderDetail);
        break;
      default:
        break;
    }
    if (nextPaymentItems.length) {
      membershipOverview = {
        ...membershipOverview,
        overview: {
          ...membershipOverview?.overview,
          body: currentMebership.membershipOverview.overview.body,
          renewDate: orderTime,
          subheading: orderType
        }
      };
      paymentDetails = {
        ...currentMebership?.paymentDetails,
        nextPayment: {
          ...currentMebership?.paymentDetails?.nextPayment,
          heading: {
            ...currentMebership?.paymentDetails?.nextPayment?.heading,
            value: orderTime
          },
          items: nextPaymentItems
        },
        summary: {
          ...currentMebership?.paymentDetails?.summary,
          items: summaryItem
        }
      };
    }
    if (currentCepMebership) {
      membershipOverview = {
        ...currentCepMebership
      };
    }
    let newCurrentMebership = { ...currentMebership };
    Object.assign(newCurrentMebership, {
      membershipOverview,
      paymentDetails,
      needHelp
    });
    return newCurrentMebership;
  };
  return { setNextPaymentAndSummary };
};

export default useMembershipOrderDetail;
